/**
 * Normalizes floating point precision errors by rounding to specified decimal places
 * @param value - The number value to normalize
 * @param scale - Number of decimal places to round to (default: 2)
 * @returns Normalized number value
 */
export function normalizeFloatingPoint(
  value: number,
  scale: number = 2
): number {
  if (!value || isNaN(value)) return value

  const factor = Math.pow(10, scale)
  const rounded = Math.round(value * factor) / factor

  return rounded
}

/**
 * Performs percentage calculation with floating point normalization
 * @param total - The total value (e.g., 100 for percentages)
 * @param value - The value to subtract from total
 * @param scale - Number of decimal places to round to (default: 2)
 * @returns Normalized remainder value as number
 */
export function calculatePercentageRemainder(
  total: number,
  value: number,
  scale: number = 2
): number {
  if (isNaN(value) || value < 0) return normalizeFloatingPoint(total, scale)

  const remainder = total - value
  return normalizeFloatingPoint(remainder, scale)
}

/**
 * Counts the decimal places in a numeric string, including scientific notation
 * @param value - The numeric string (e.g., "0.0025" or "5e-3")
 * @returns Number of decimal places; negative for positive exponents
 */
export function decimalScale(value: string): number {
  const [mantissa, exponent = "0"] = value.toLowerCase().split("e")
  const fraction = mantissa.split(".")[1] ?? ""

  return fraction.length - Number(exponent)
}
